/**
 * OtterAuth error mapping (04-architecture § 11.4).
 *
 * The SDK's raw codes are mapped once, here; a raw message is never rendered.
 * The codes are OtterAuth's own machine codes (`ApiError.code`) as named in its
 * `llm.txt`. That file names the codes of the TOTP step exhaustively but not
 * those of the OTP step, so the status fallback below carries the cases it
 * leaves unnamed rather than guessing at code strings that may not exist.
 *
 * `code` is **not** always a machine code. The SDK's `readError` copies a
 * string `body.error` into both `message` and `code`, and some responses put
 * prose there — a bad address comes back as `code: 'Validation failed'`. So the
 * table is a lookup of known values, never a source of anything user-facing:
 * an unmatched code falls through to the status, and the kinds it produces are
 * a closed union that `authErrorKey` turns into a key of our own.
 */

export type AuthErrorKind =
  | 'otp_invalid'
  | 'otp_expired'
  | 'otp_rate_limited'
  | 'totp_invalid'
  | 'oauth_cancelled'
  | 'oauth_failed'
  | 'email_invalid'
  | 'network'
  | 'unknown';

const RAW_ERRORS: Record<string, AuthErrorKind> = {
  // llm.txt § 4 "TOTP", Notes.
  'invalid-totp-code': 'totp_invalid',
  'invalid-or-expired-totp-token': 'otp_expired',
  // The token was minted in the other OtterAuth environment (test vs live).
  // Nothing the user can act on, so it reads as the generic failure.
  'environment-mismatch': 'unknown',
  // Only reachable with `failIfUserNotFound`, which none of our flows passes —
  // mapped anyway so a future login-only flow cannot leak the raw code.
  'user-not-found': 'otp_invalid',
  // llm.txt § 4 "OAuth", Notes.
  'invalid-google-id-token': 'oauth_failed',
  'invalid-apple-id-token': 'oauth_failed',
  'oauth-provider-linked-to-another-account': 'oauth_failed',
  'provider-not-linked': 'oauth_failed',
  // KnownErrorCode, on a call made with a token that has already died.
  'invalid-or-expired-session': 'unknown',
  'missing-or-invalid-session-token': 'unknown',
  'no-session': 'unknown',
};

/**
 * Which call failed, so the status fallback can say something true.
 *
 * `otp_invalid` reads "Der Code stimmt nicht. Schau nochmal in die E-Mail." —
 * right for a wrong e-mail code, wrong for anything else. A 400 from TOTP
 * enrolment told an admin to check their inbox, which is the kind of message
 * that costs someone twenty minutes. Only the step that *is* about a mailed
 * code claims a bad code; every other caller falls through to the generic one.
 */
export type AuthContext = 'otp' | 'totp' | 'generic';

export function mapAuthError(error: unknown, context: AuthContext = 'generic'): AuthErrorKind {
  const code = (error as { code?: string } | null)?.code;
  if (code && RAW_ERRORS[code]) return RAW_ERRORS[code];

  const name = (error as { name?: string } | null)?.name;
  if (name === 'TypeError' || name === 'AbortError') return 'network';

  // llm.txt § 5: 429 is the rate limiter, and a refused code comes back as a
  // 400 or a 401 depending on which step refused it.
  const status = (error as { status?: number } | null)?.status;
  if (status === 429) return 'otp_rate_limited';
  if (status === 400 || status === 401 || status === 404) {
    if (context === 'otp') return 'otp_invalid';
    if (context === 'totp') return 'totp_invalid';
  }
  return 'unknown';
}

export function authErrorKey(kind: AuthErrorKind): string | null {
  // A cancelled native sheet is not an error the user needs to read.
  if (kind === 'oauth_cancelled') return null;
  if (kind === 'network') return 'errors.network_error';
  if (kind === 'unknown') return 'errors.unknown';
  return `authErrors.${kind}`;
}
